import type { Input } from '../sim/car';

/**
 * Per-browser saves (design.md B12.5). localStorage can be unavailable or
 * wiped (private mode, cleared data), so every access is wrapped and the
 * game works without it. Career data moves to IndexedDB in M5.
 */

export interface GhostStart {
  x: number;
  y: number;
  vx: number;
  vy: number;
  h: number;
  bonusMph: number;
  streakDist: number;
}

export interface GhostData {
  v: 1;
  track: string;
  team: string;
  ticks: number;
  start: GhostStart;
  /** Checkpoint split times (ticks from lap start), index = gate. */
  splits: number[];
  inputs: Input[];
}

function get(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function set(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / disabled: play on without saving */
  }
}

export function loadBest(track: string, team: string): number | null {
  const v = get(`sms.best.${track}.${team}`);
  const n = v === null ? NaN : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function saveBest(track: string, team: string, ticks: number): void {
  set(`sms.best.${track}.${team}`, String(ticks));
}

function encodeInputs(inputs: Input[]): string {
  const bytes = new Int8Array(inputs.length * 3);
  inputs.forEach((i, k) => {
    bytes[k * 3] = i.steer;
    bytes[k * 3 + 1] = i.throttle;
    bytes[k * 3 + 2] = i.brake;
  });
  let s = '';
  const u = new Uint8Array(bytes.buffer);
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
  return btoa(s);
}

function decodeInputs(b64: string): Input[] {
  const s = atob(b64);
  const u = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
  const b = new Int8Array(u.buffer);
  const out: Input[] = [];
  for (let k = 0; k + 2 < b.length; k += 3) out.push({ steer: b[k], throttle: b[k + 1], brake: b[k + 2] });
  return out;
}

export function saveGhost(g: GhostData): void {
  set(`sms.ghost.${g.track}.${g.team}`, JSON.stringify({ ...g, inputs: encodeInputs(g.inputs) }));
}

export function loadGhost(track: string, team: string): GhostData | null {
  const raw = get(`sms.ghost.${track}.${team}`);
  if (!raw) return null;
  try {
    const o = JSON.parse(raw);
    if (o.v !== 1) return null;
    return { ...o, inputs: decodeInputs(o.inputs) };
  } catch {
    return null;
  }
}

export function loadSetting(key: string, fallback: string): string {
  return get(`sms.set.${key}`) ?? fallback;
}

export function saveSetting(key: string, value: string): void {
  set(`sms.set.${key}`, value);
}

export interface Stats {
  races: number;
  wins: number;
  podiums: number;
  points: number;
}

export function loadStats(): Stats {
  try {
    const o = JSON.parse(get('sms.stats') ?? 'null');
    if (o && typeof o.races === 'number') return o as Stats;
  } catch {
    /* fall through */
  }
  return { races: 0, wins: 0, podiums: 0, points: 0 };
}

export function saveStats(s: Stats): void {
  set('sms.stats', JSON.stringify(s));
}

/** The season in progress (versioned JSON; see src/sim/season.ts). */
export function loadSeasonRaw(): unknown {
  try {
    return JSON.parse(get('sms.season') ?? 'null');
  } catch {
    return null;
  }
}

export function saveSeasonRaw(s: unknown): void {
  set('sms.season', JSON.stringify(s));
}

export function clearSeason(): void {
  try {
    localStorage.removeItem('sms.season');
  } catch {
    /* ignore */
  }
}
